/**
 * Pilgrim recently viewed — storage + rendering for Recently viewed (PILGRIM).
 *
 * Owner: PILGRIM. Pairs with blocks/pilgrim-recently-viewed.liquid and
 * snippets/pilgrim-recently-viewed-tracker.liquid.
 *
 * Why not Horizon's native RecentlyViewed (assets/recently-viewed-products.js)?
 * It caps at 4 products and feeds the search drawer; raising its cap would be a
 * native-file edit that also lengthens that drawer list. This is an isolated
 * copy of the same idea with its own storage key and a 12-product cap. The
 * tracker snippet (rendered site-wide by sections/pilgrim-header.liquid)
 * records every product-page visit; both lists coexist.
 *
 * The custom element follows the pilgrim-product-list tag-search pattern: the block
 * server-renders a neutral gray skeleton row (the native recommendations
 * loading look — NOT placeholder cards, whose t-shirt art reads as fake
 * products), and the real cards come from Shopify's search index via the
 * Search + Section Rendering API (q=id:… OR id:…&section_id=…). The fetch
 * fires when the block nears the viewport — or, because a hidden tab panel
 * never intersects, from a short prefetch timer after connect, so by the time
 * a shopper opens a Recently viewed tab the real cards are usually already in. Recency order
 * is preserved server-side: the block re-render reads the requested order back
 * out of search.terms. The current product is excluded on PDPs via
 * data-exclude-id (a visit records itself first, so it would always rank #1).
 *
 * Empty contract with Tabs (PILGRIM): with no (renderable) history the element
 * hides itself, sets data-pilgrim-empty, and dispatches a bubbling
 * `pilgrim:tab-empty` — inside a tab panel this removes the whole tab
 * (skipped by the tabs element in the theme editor).
 *
 * Carousel arrows: Horizon's slideshow hides its controls only for 0–1 slides,
 * so with e.g. two viewed products the next-arrow still appears on hover even
 * though nothing can scroll. A ResizeObserver on each scroller shows the
 * arrows only while the slides actually overflow.
 */

export class PilgrimViewedProducts {
  static #STORAGE_KEY = 'pilgrim:viewedProducts';
  static #MAX_PRODUCTS = 12;

  /** @param {string} productId */
  static addProduct(productId) {
    try {
      let viewed = this.getProducts();
      viewed = viewed.filter((/** @type {string} */ id) => id !== productId);
      viewed.unshift(productId);
      localStorage.setItem(PilgrimViewedProducts.#STORAGE_KEY, JSON.stringify(viewed.slice(0, PilgrimViewedProducts.#MAX_PRODUCTS)));
    } catch {
      // Storage unavailable (private mode, blocked) — tracking is best-effort.
    }
  }

  /** @returns {string[]} most recent first */
  static getProducts() {
    try {
      const raw = JSON.parse(localStorage.getItem(PilgrimViewedProducts.#STORAGE_KEY) || '[]');
      return Array.isArray(raw) ? raw.map(String) : [];
    } catch {
      return [];
    }
  }
}

class PilgrimRecentlyViewed extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #arrowsObserver = null;
  /** @type {number | undefined} */
  #prefetchTimer;
  #loaded = false;

  connectedCallback() {
    // The fetched re-render of this element must not re-run the lazy logic.
    if (this.dataset.performed === 'true') return;

    const ids = this.#wantedIds();

    if (ids.length === 0) {
      this.#markEmpty();
      return;
    }

    this.#trimSkeleton(ids.length);
    this.#watchCarouselOverflow();

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#load(ids);
      },
      // Begin fetching a little before the block scrolls (or tabs) into view.
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#observer.observe(this);

    // A hidden tab panel never intersects, so without a fallback the first
    // open would always show the loading row while the fetch runs. Prefetch
    // shortly after connect — one small search request, well after the
    // above-the-fold work — so the real cards are usually in place before the
    // shopper reaches the tab. #load guards against the observer double-firing.
    this.#prefetchTimer = setTimeout(() => this.#load(ids), 2500);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#arrowsObserver?.disconnect();
    clearTimeout(this.#prefetchTimer);
  }

  /**
   * Show carousel arrows only while the slides overflow the scroller — the
   * native slideshow keeps them for any 2+ slides, even when everything fits.
   * The observer also re-evaluates when a surrounding tab reveals the block.
   */
  #watchCarouselOverflow() {
    this.#arrowsObserver?.disconnect();
    const scrollers = this.querySelectorAll('slideshow-slides');
    if (scrollers.length === 0) return;

    this.#arrowsObserver = new ResizeObserver(() => this.#toggleArrows());
    for (const scroller of scrollers) {
      this.#arrowsObserver.observe(scroller);
    }
  }

  #toggleArrows() {
    for (const scroller of this.querySelectorAll('slideshow-slides')) {
      const arrows = scroller.parentElement?.querySelector('slideshow-arrows');
      if (!(arrows instanceof HTMLElement)) continue;
      const scrollable = scroller.scrollWidth > scroller.clientWidth + 1;
      arrows.style.display = scrollable ? '' : 'none';
    }
  }

  /** @returns {string[]} stored ids minus the current product, capped to the block's max */
  #wantedIds() {
    const max = Number.parseInt(this.dataset.max || '8', 10);
    return PilgrimViewedProducts.getProducts()
      .filter((id) => id !== this.dataset.excludeId)
      .slice(0, max);
  }

  /**
   * The skeleton row reserves space for the block's max products; the visitor
   * may have fewer. Boxes beyond the real count are dropped up front so the
   * swap doesn't collapse a gap (the skeleton is always the grid row, for
   * carousel layouts styled as one clipped line).
   * @param {number} count
   */
  #trimSkeleton(count) {
    const items = this.querySelectorAll('.resource-list--grid .resource-list__item');
    for (let index = items.length - 1; index >= count; index -= 1) {
      items[index].remove();
    }
  }

  /** @param {string[]} ids */
  async #load(ids) {
    if (this.#loaded) return;
    this.#loaded = true;
    this.#observer?.disconnect();

    const { searchUrl, sectionId } = this.dataset;
    if (!searchUrl || !sectionId) return;

    // One query for all ids; the section re-render restores recency order from
    // search.terms. Sold-out products stay visible, like everywhere else.
    const query =
      `q=${encodeURIComponent(ids.map((id) => `id:${id}`).join(' OR '))}` +
      `&type=product` +
      `&options[unavailable_products]=show` +
      `&section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(`${searchUrl}?${query}`);
      if (!response.ok) throw new Error(`Search returned ${response.status}`);

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = markup.querySelector(`pilgrim-recently-viewed[id="${this.id}"]`);
      if (!fresh) throw new Error('Recently viewed markup not found in search response');

      const flag = fresh.querySelector('[data-has-products]');
      if (flag && flag.getAttribute('data-has-products') === 'false') {
        this.#markEmpty();
        return;
      }

      this.innerHTML = fresh.innerHTML;
      this.#stripSearchTracking();
      this.removeAttribute('aria-busy');
      this.#watchCarouselOverflow();
    } catch (error) {
      // In the Theme Editor a freshly-added section's id may not resolve through
      // the Section Rendering API yet; leave the skeleton rather than erroring.
      if (!window.Shopify || !window.Shopify.designMode) this.#markEmpty();
      console.warn('[pilgrim-recently-viewed]', error instanceof Error ? error.message : error);
    }
  }

  /**
   * The cards come from a search render, so Shopify adds its search click
   * tracking (_pos/_sid/_ss) to every link. These are not searches: link to
   * the bare URL (pilgrim-docs/features/clean-urls.md).
   */
  #stripSearchTracking() {
    for (const link of this.querySelectorAll('a[href*="_ss="]')) {
      const url = new URL(link.href);
      for (const name of ['_pos', '_sid', '_ss']) url.searchParams.delete(name);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    }
  }

  /** No history to show: hide, and let a surrounding tab remove itself too. */
  #markEmpty() {
    // Keep the skeleton visible for merchants in the editor (their preview
    // session usually has no browsing history).
    if (window.Shopify && window.Shopify.designMode) return;

    this.hidden = true;
    this.setAttribute('data-pilgrim-empty', '');
    this.dispatchEvent(new CustomEvent('pilgrim:tab-empty', { bubbles: true }));
  }
}

if (!customElements.get('pilgrim-recently-viewed')) {
  customElements.define('pilgrim-recently-viewed', PilgrimRecentlyViewed);
}
